/*
 * Cart page enhancements. Everything here is additive: the page works with
 * JavaScript switched off, where quantity, removal and the note are plain
 * form posts (see sections/main-cart.liquid).
 *
 * Three things live here and nowhere else:
 *
 *   Save for later — Shopify's cart has no such list, so it is kept in
 *   localStorage on this device. Restoring re-adds the variant.
 *
 *   Undo a removal — the removed line is held in memory for a few seconds so
 *   it can be added back. Nothing is stored; a reload forgets it, which is
 *   the honest behaviour for a five-second window.
 *
 *   Autosaving the note, so it survives a quantity change (which reloads the
 *   section) instead of only riding along to checkout.
 *
 * Cart mutations go through window.themeCart.sync() (assets/cart.js) so the
 * header count and the drawer end up in the same state a form post would
 * have left them in.
 */
(function () {
  'use strict';

  var UNDO_MS = 8000;

  var pendingUndo = null;
  var undoTimer = null;
  var pendingQuantity = null;
  var strings = window.themeStrings || {};

  function interpolate(template, values) {
    return Object.keys(values || {}).reduce(function (text, key) {
      return text.split('{' + key + '}').join(String(values[key]));
    }, template || '');
  }

  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  /* Samma hjälpare som i assets/cart.js, duplicerad med flit: temat har ingen
     modulkedja, och `window.themeCart` är seamen för vagnmutationer — inte en
     plats att hänga textutskrifter på. Texten sätts i en egen tick eftersom en
     aria-live-region som får samma sträng igen inte är någon mutation och
     därför inte läses upp. */
  function announce(text) {
    var region = document.getElementById('a11y-status');
    if (!region || !text) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = text;
    }, 60);
  }

  /* ---------------------------------------------------------------- storage */

  function readSaved() {
    if (window.themeSavedProducts) return window.themeSavedProducts.read();
    try {
      var raw = window.localStorage.getItem('hg-cart-saved');
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      /* Private browsing, a full quota, or a value some other tab corrupted.
         An unusable list is not worth breaking the cart page over. */
      return [];
    }
  }

  function writeSaved(list) {
    if (window.themeSavedProducts) return window.themeSavedProducts.write(list, { source: 'cart' });
    try {
      window.localStorage.setItem('hg-cart-saved', JSON.stringify(list));
    } catch (error) {
      /* Same as above — the in-memory render below still reflects the click. */
    }
  }

  /* ------------------------------------------------------------ section swap */

  /*
   * Fokus över sektionsswappen. `replaceWith()` nedan tar bort den nod fokus
   * står i, och webbläsaren lägger då fokus på <body> — tangentbordet tappar
   * alltså sin plats i listan vid varje antalsändring. Radens dolda id-fält bär
   * `item.key`, oförändrad över en antalsändring, så raden går att slå upp igen
   * i den färska markupen.
   */
  function rowControls(row) {
    return qsa('a[href], button', row);
  }

  function rowKey(row) {
    var input = qs('input[name="id"]', row);
    return input ? input.value : '';
  }

  function captureFocus() {
    var active = document.activeElement;
    var page = qs('[data-cart-page]');
    if (!page || !active || !page.contains(active)) return null;

    var row = active.closest('[data-cart-row]');
    if (!row) return null;
    return { key: rowKey(row), index: rowControls(row).indexOf(active) };
  }

  function restoreFocus(snapshot) {
    if (!snapshot) return;

    var target = null;
    if (snapshot.key) {
      qsa('[data-cart-row]').some(function (row) {
        if (rowKey(row) !== snapshot.key) return false;
        target = rowControls(row)[snapshot.index] || null;
        return true;
      });
    }

    if (target) {
      target.focus();
      return;
    }

    /* Raden finns inte kvar (borttagen eller sparad). Fokus landar då på
       radlistan, som ligger kvar på samma plats i sidan — tabindex plockas
       bort vid blur så listan inte blir ett eget tabbstopp resten av besöket. */
    var lines = qs('[data-cart-lines]') || qs('[data-cart-page]');
    if (!lines) return;
    lines.setAttribute('tabindex', '-1');
    lines.addEventListener('blur', function handler() {
      lines.removeAttribute('tabindex');
      lines.removeEventListener('blur', handler);
    });
    lines.focus();
  }

  /* Summan har ingen egen data-krok i markupen — den är värdespannet i
     totalsraden. Saknas den annonseras bara antalet. */
  function cartTotal() {
    var row = qs('.cart__total-row--sum');
    var value = row && row.lastElementChild;
    return value ? value.textContent.trim() : '';
  }

  function refreshCart() {
    var current = qs('[data-cart-page]');
    var sectionId = current && current.dataset.sectionId;
    if (!sectionId) return Promise.resolve();

    var focusSnapshot = captureFocus();

    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (response) {
        /* Utan kollen parsas en felsida som om den vore sektionen, `fresh` blir
           null och sidan står kvar med gamla siffror utan att någon märker
           det. Ett kastat fel går till .catch nedan. */
        if (!response.ok) throw new Error('section render failed');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-page]');
        var target = qs('[data-cart-page]');
        if (fresh && target) {
          target.replaceWith(fresh);
          restoreFocus(focusSnapshot);
        }
      })
      .catch(function () {
        /* The mutation itself already succeeded; only redrawing failed. A
           reload shows the cart as the server has it. */
        window.location.reload();
      });
  }

  /* -------------------------------------------------------------------- undo */

  function clearUndo() {
    pendingUndo = null;
    if (undoTimer) {
      window.clearTimeout(undoTimer);
      undoTimer = null;
    }
    var bar = qs('[data-cart-undo]');
    if (bar) bar.hidden = true;
  }

  function renderUndo() {
    var bar = qs('[data-cart-undo]');
    if (!bar || !pendingUndo) return;

    var text = qs('[data-cart-undo-text]', bar);
    if (text) text.textContent = interpolate(strings.cartRemoved || '{title} togs bort.', { title: pendingUndo.title });
    bar.hidden = false;

    /* Baren dyker upp utan att fokus flyttas dit, så den som inte ser skärmen
       får bara veta att den finns om den sägs — och hur länge den står kvar,
       eftersom den försvinner av sig själv. Sägs en gång per borttagning:
       funktionen körs om vid varje senare vagnändring inom ångra-fönstret. */
    if (!pendingUndo.announced) {
      pendingUndo.announced = true;
      announce(interpolate(strings.cartUndoAvailable || '{title} togs bort. Ångra-knappen ligger kvar i åtta sekunder.', { title: pendingUndo.title }));
    }

    undoTimer = window.setTimeout(clearUndo, UNDO_MS);
  }

  function undoRemoval() {
    if (!pendingUndo) return;
    var line = pendingUndo;
    clearUndo();

    addToCart(line.variantId, line.quantity).then(function (added) {
      /* Nekades tillägget har showCartError redan skrivit ut serverns egen
         förklaring. Baren armas inte om: nekandet är ett lagerbesked och ett
         nytt försök hade fallit på samma sak. */
      if (added) announce(interpolate(strings.cartRestoredAnnouncement || '{title} lades tillbaka i varukorgen.', { title: line.title }));
    });
  }

  /* ------------------------------------------------------------- mutations */

  /* Ångra och "lägg i varukorgen" från sparat är JS-ägda ytor och har därför
     ingen felyta i Liquid. Noden skapas här, på samma plats som ångra-baren,
     och nästa sektionsomritning tar den med sig. `.field__error` är temats
     befintliga stil för en felrad. */
  function showCartError(message) {
    var page = qs('[data-cart-page]');
    if (!page) return;

    var errorEl = qs('[data-cart-inline-error]', page);
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'field__error';
      errorEl.setAttribute('data-cart-inline-error', '');
      var anchor = qs('[data-cart-undo]', page) || qs('[data-cart-lines]', page);
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(errorEl, anchor);
      } else {
        page.insertBefore(errorEl, page.firstChild);
      }
    }

    errorEl.textContent = message;
    announce(message);
  }

  /*
   * Löser till true om varan verkligen hamnade i vagnen.
   *
   * Anroparna behöver veta skillnaden: de har egna ytor att hålla i ordning —
   * posten i sparat-listan, ångra-baren — och en `window.location.reload()`
   * som enda felhantering hade dolt VARFÖR det inte gick och dessutom tagit
   * med sig det kunden just gjorde. Ett nekat tillägg (slut i lager, borttagen
   * variant) visas därför inline med serverns egen text. Reload är kvar för
   * det fall där den är rätt: när varan mycket väl kan ligga i vagnen och det
   * bara är svaret eller omritningen efteråt som gick fel — då är serverns
   * egen sida den enda sanningen.
   */
  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (data) {
            return { ok: response.ok, data: data };
          });
      })
      .then(function (result) {
        if (!result.ok) {
          showCartError((result.data && result.data.description) || strings.cartAddError || 'Varan kunde inte läggas i varukorgen.');
          return false;
        }

        return Promise.resolve(window.themeCart ? window.themeCart.sync() : null)
          .then(refreshCart)
          .then(renderSaved)
          .then(function () {
            return true;
          });
      })
      .catch(function () {
        window.location.reload();
        return false;
      });
  }

  /* ------------------------------------------------------------ saved list */

  function savedPrice(price) {
    if (!price) return '';
    if (typeof price === 'string' && !/^\d+(\.\d+)?$/.test(price)) return price;
    var page = qs('[data-cart-page]');
    var locale = (page && page.getAttribute('data-shipping-locale')) || document.documentElement.lang || 'sv-SE';
    var currency = (page && page.getAttribute('data-shipping-currency')) || 'SEK';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(price) / 100);
  }

  function savedProductUrl(entry) {
    try {
      var url = new URL(entry.url || '', window.location.origin);
      if (url.origin === window.location.origin && /\/products\//.test(url.pathname)) {
        return url.pathname + url.search;
      }
    } catch (error) {
      /* Fall through to the handle-derived path below. */
    }

    var handle = entry.handle || '';
    if (/^[a-z0-9-]+$/i.test(handle)) {
      return '/products/' + encodeURIComponent(handle) + '?variant=' + encodeURIComponent(entry.variantId);
    }
    return '#';
  }

  function savedRow(entry) {
    var row = document.createElement('div');
    row.className = 'cart__saved-row';

    var media = document.createElement('div');
    media.className = 'media media--fit media--square cart__saved-media';
    if (entry.image) {
      var img = document.createElement('img');
      img.src = entry.image;
      img.alt = entry.title;
      img.width = 60;
      img.height = 60;
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      media.className += ' media--empty';
    }
    row.appendChild(media);

    var body = document.createElement('div');
    body.className = 'cart__saved-body';

    var title = document.createElement('a');
    title.className = 'cart__saved-title';
    title.href = savedProductUrl(entry);
    /* textContent, never innerHTML: the title came out of localStorage, which
       any script on this origin can write to. */
    title.textContent = entry.title;
    body.appendChild(title);

    var price = document.createElement('span');
    price.className = 'cart__saved-price';
    price.textContent = savedPrice(entry.price);
    body.appendChild(price);

    row.appendChild(body);

    var restore = document.createElement('button');
    restore.type = 'button';
    restore.className = 'cart__saved-restore';
    restore.textContent = strings.addToCart || 'Lägg i varukorgen';
    restore.dataset.savedRestore = String(entry.variantId);
    row.appendChild(restore);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart__saved-remove';
    remove.setAttribute('aria-label', (strings.removeSaved || 'Ta bort från sparat') + ': ' + entry.title);
    remove.textContent = '✕';
    remove.dataset.savedRemove = String(entry.variantId);
    row.appendChild(remove);

    return row;
  }

  function renderSaved() {
    var container = qs('[data-cart-saved]');
    var list = qs('[data-cart-saved-list]');
    var all = qs('[data-cart-saved-all]');
    if (!container || !list) return;

    var entries = readSaved();
    list.textContent = '';

    if (entries.length === 0) {
      container.hidden = true;
      return;
    }

    entries.slice(0, 3).forEach(function (entry) {
      list.appendChild(savedRow(entry));
    });
    if (all) {
      all.hidden = entries.length <= 3;
      all.textContent = 'Visa alla ' + entries.length + ' sparade produkter';
    }
    container.hidden = false;
  }

  /* ------------------------------------------------------------------ events */

  document.addEventListener('click', function (event) {
    var save = event.target.closest('[data-cart-save]');
    if (save) {
      var savedEntry = {
        variantId: save.dataset.variantId,
        quantity: parseInt(save.dataset.quantity, 10) || 1,
        handle: save.dataset.handle || '',
        title: save.dataset.title || '',
        variantTitle: save.dataset.variantTitle || '',
        vendor: save.dataset.vendor || '',
        url: save.dataset.url || '',
        price: Number(save.dataset.price) || 0,
        image: save.dataset.image || '',
      };
      if (window.themeSavedProducts) {
        window.themeSavedProducts.add(savedEntry, { source: 'cart-save-later' });
      } else {
        var entries = readSaved();
        entries.push(savedEntry);
        writeSaved(entries);
      }

      /* Removing the line is exactly what the row's own remove form does, so
         submit that rather than repeating the fetch — assets/cart.js then
         updates the header and the drawer as usual. Undo would be wrong
         here: the line was not lost, it moved. */
      clearUndo();
      var row = save.closest('[data-cart-row]');
      var removeForm = row && qs('[data-cart-remove-form]', row);
      if (removeForm) {
        save.dataset.movedToSaved = 'true';
        announce((save.dataset.title || 'Artikeln') + ' flyttades till sparade produkter.');
        removeForm.requestSubmit();
      }
      return;
    }

    var undo = event.target.closest('[data-cart-undo-button]');
    if (undo) {
      undoRemoval();
      return;
    }

    var restore = event.target.closest('[data-saved-restore]');
    if (restore) {
      var restoreId = String(restore.dataset.savedRestore || '');
      var entry = readSaved().find(function (savedEntry) {
        return savedEntry.variantId === restoreId;
      });
      if (!entry) return;
      restore.disabled = true;

      addToCart(entry.variantId, entry.quantity).then(function (added) {
        if (!added) {
          /* Posten ligger kvar i sparat — den ströks inte före anropet, just
             för att ett nekat tillägg annars hade tagit både varan och minnet
             av den. Knappen går att trycka igen. */
          restore.disabled = false;
          return;
        }

        if (window.themeSavedProducts) {
          window.themeSavedProducts.remove(entry.variantId, { source: 'cart-restore' });
        } else {
          writeSaved(readSaved().filter(function (savedEntry) {
            return savedEntry.variantId !== entry.variantId;
          }));
        }
        renderSaved();
        announce(entry.title + ' lades i varukorgen.');
      });
      return;
    }

    var removeSaved = event.target.closest('[data-saved-remove]');
    if (removeSaved) {
      var removeId = String(removeSaved.dataset.savedRemove || '');
      if (!removeId) return;
      if (window.themeSavedProducts) {
        window.themeSavedProducts.remove(removeId, { source: 'cart-preview' });
      } else {
        writeSaved(readSaved().filter(function (savedEntry) {
          return String(savedEntry.variantId) !== removeId;
        }));
      }
      renderSaved();
    }
  });

  /*
   * Runs alongside the handler in assets/cart.js, which does the actual
   * removal. This one only takes the snapshot, and it has to happen here:
   * once the section is re-rendered the row — and its data attributes — are
   * gone.
   */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches || !form.matches('[data-cart-change-form]')) return;
    /* Lådans rader postar samma sorts formulär men annonseras av cart.js. */
    if (!form.closest('[data-cart-page]')) return;

    if (!form.matches('[data-cart-remove-form]')) {
      /* Antalet läses av här och inte efter swappen: den nya markupen säger
         inte vilken rad som ändrades, bara hur vagnen ser ut nu. */
      pendingQuantity = parseInt(new FormData(form).get('quantity'), 10);
      return;
    }

    var button = qs('.cart__remove', form);
    var movedToSaved = qs('[data-cart-save][data-moved-to-saved]', form.closest('[data-cart-row]') || form);
    if (!button || movedToSaved) return;

    pendingUndo = {
      variantId: button.dataset.variantId,
      quantity: parseInt(button.dataset.quantity, 10) || 1,
      title: button.dataset.lineTitle || 'Artikeln',
    };
  });

  /* Autosave the note. `change` fires on blur once the value actually
     differs, so this is one request per edit, not one per keystroke. */
  document.addEventListener('change', function (event) {
    var note = event.target.closest('[data-cart-note]');
    if (!note) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: note.value }),
    }).catch(function () {
      /* Left as-is: the note is still in the field and still posts with the
         checkout button, which is the no-JS path anyway. */
    });
  });

  /*
   * assets/cart.js announces every cart mutation. Redraw from the server on
   * each one, then put back the two pieces of state the server does not know
   * about.
   */
  document.addEventListener('cart:updated', function () {
    refreshCart().then(function () {
      renderSaved();
      renderUndo();

      /* Efter omritningen, annars vore summan den gamla. renderUndo() äger
         borttagningsfallet och har redan sagt sitt. */
      if (pendingQuantity === null || isNaN(pendingQuantity)) {
        pendingQuantity = null;
        return;
      }
      var total = cartTotal();
      announce('Antal ' + pendingQuantity + '.' + (total ? ' Totalt ' + total + '.' : ''));
      pendingQuantity = null;
    });
  });

  document.addEventListener('saved-products:updated', renderSaved);

  renderSaved();
})();
